import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://passalgarve.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

export default function Seo({
  title,
  description,
  image,
  noindex = false,
  type = "website",
  jsonLd,
  canonicalPath,
  ptPath: ptPathProp,
  enPath: enPathProp,
  keywords,
}) {
  const { pathname } = useLocation();
  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const ptPath = ptPathProp || canonicalPath || (isEn ? (pathname.replace(/^\/en/, "") || "/") : pathname);
  const enPath = enPathProp || (ptPath === "/" ? "/en" : `/en${ptPath}`);
  const ptUrl = `${SITE_URL}${ptPath}`;
  const enUrl = `${SITE_URL}${enPath}`;
  const canonical = isEn ? enUrl : ptUrl;
  const fullTitle = title
    ? (title.includes("PASS Algarve") ? title : `${title} | PASS Algarve`)
    : "PASS Algarve | Atrações e Experiências no Algarve com um só passe";
  const desc = description ||
    "Compre o PASS Algarve e viva as melhores atrações, passeios de barco, golfinhos e experiências em Lagos e no Algarve com um único QR Code.";
  const img = image || DEFAULT_IMAGE;
  const jsonLdList = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="pt-PT" href={ptUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={ptUrl} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      <meta property="og:site_name" content="PASS Algarve" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={isEn ? "en_US" : "pt_PT"} />
      <meta property="og:locale:alternate" content={isEn ? "pt_PT" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLdList.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}

export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "PASS Algarve",
  url: SITE_URL,
  logo: DEFAULT_IMAGE,
  email: "info@passalgarve.com",
  address: { "@type": "PostalAddress", addressLocality: "Lagos", addressRegion: "Algarve", addressCountry: "PT" },
};

export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "PASS Algarve",
  url: SITE_URL,
  inLanguage: ["pt-PT", "en"],
};

const langPath = (path, en) => (en ? (path === "/" ? "/en" : `/en${path}`) : path);

export const breadcrumbJsonLd = (items, en = false) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${langPath(it.path, en)}`,
  })),
});

export const attractionJsonLd = (a, path, en = false) => {
  const obj = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: (en && a.name_en) || a.name,
    description: en
      ? (a.seo_description_en || a.description_en || a.description)
      : (a.seo_description || a.description),
    url: `${SITE_URL}${path}`,
    image: (en ? (a.social_image_en || a.social_image) : a.social_image) || a.image_url || undefined,
    address: { "@type": "PostalAddress", addressLocality: "Lagos", addressRegion: "Algarve", addressCountry: "PT" },
  };
  return obj;
};

export const passportJsonLd = (pt, path, en = false) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: (en && pt.name_en) || pt.name,
  description: (en ? (pt.seo_description_en || pt.description_en || pt.description) : (pt.seo_description || pt.description)) || pt.tagline || undefined,
  image: (en ? (pt.social_image_en || pt.social_image) : pt.social_image) || pt.image_url || DEFAULT_IMAGE,
  url: `${SITE_URL}${path}`,
  brand: { "@type": "Organization", name: "PASS Algarve" },
  offers: {
    "@type": "Offer",
    price: String(pt.price ?? ""),
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}${path}`,
  },
});
